import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { DocumentTypeRecord } from "@/lib/catalog/documentTypesStore";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";

type DocumentTypeSelectProps = {
  value?: string | null;
  options: DocumentTypeRecord[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
};

export function DocumentTypeSelect({
  value,
  options,
  onChange,
  placeholder = "Select type",
  disabled = false,
  triggerClassName,
}: DocumentTypeSelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);

  const selected = options.find((t) => t.name === value);
  const displayLabel = selected?.name ?? (value?.trim() ? value : placeholder);

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-10 w-full justify-between font-normal text-sm px-3",
            !selected && !value && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start" sideOffset={4}>
        <Command shouldFilter>
          <CommandInput {...commandInputProps} placeholder="Search type..." className="h-9 text-xs" />
          <CommandList className="max-h-[220px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No type found.
            </CommandEmpty>
            <CommandGroup>
              {options.map((t) => {
                const isSelected = t.name === value;
                return (
                  <CommandItem
                    key={t.id}
                    value={t.name}
                    onSelect={() => {
                      onChange(t.name);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="truncate">{t.name}</span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
